import React, { useEffect, useState } from 'react';
import { Package, HardHat, Truck, Layers, Box, Building2, CalendarClock, Ruler } from 'lucide-react';
import { api } from '../../../utils/api';
import { fmt } from '../../../lib/projectVisuals';
import { sum } from './materialUtils';

const Row = ({ icon: Icon, label, value, pct }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="w-8 h-8 rounded-lg bg-stone-100 text-stone-600 flex items-center justify-center"><Icon size={15} /></span>
    <span className="flex-1 text-[13px] text-stone-700">{label}</span>
    <span className="text-[13px] font-semibold op-num">{fmt(value)} kr</span>
    <span className="w-10 text-right text-[11px] text-stone-400 op-num">{pct}%</span>
  </div>
);

const KPI = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-2.5 rounded-xl bg-stone-50 p-2.5">
    <Icon size={17} className="text-stone-500 flex-shrink-0" />
    <div className="min-w-0"><p className="text-[10.5px] text-stone-500">{label}</p><p className="text-[13px] font-bold op-num truncate" data-testid={testid}>{value}</p></div>
  </div>
);

export const ProjectOverviewCard = ({ project, plan, stats }) => {
  const [labor, setLabor] = useState(null);
  const [time, setTime] = useState(null);
  useEffect(() => {
    api.listLaborPlans(project.id).then((r) => setLabor((r.data?.plans || []).find((p) => p.status === 'completed') || null)).catch(() => {});
    api.listTimePlans(project.id).then((r) => setTime((r.data?.plans || []).find((p) => p.status === 'completed') || null)).catch(() => {});
  }, [project.id]);

  const mats = plan.materials || [];
  const material = sum(mats.filter((m) => m.category !== 'Firma-utrustning'));
  const equipment = sum(mats.filter((m) => m.category === 'Firma-utrustning'));
  const work = labor?.total_labor_cost_sek || 0;
  const machines = labor?.total_machine_cost_sek || 0;
  const markup = labor ? (labor.total_with_markup_sek || 0) - (labor.total_cost_sek || 0) : 0;
  const total = material + equipment + work + machines + markup;
  const pct = (v) => (total ? Math.round((v / total) * 100) : 0);
  const area = plan.total_area_m2 || stats?.bta;
  const budget = project.budget;

  return (
    <div className="op-card p-5" data-testid="project-overview-card">
      <h3 className="text-[15px] font-bold">Projektöversikt</h3>
      <div className="mt-3 rounded-xl border border-stone-200 p-3.5">
        <p className="text-[11.5px] text-stone-500">Total materialkostnad</p>
        <p className="text-2xl font-bold op-num" data-testid="overview-material-total">{fmt(material + equipment)} kr</p>
      </div>
      <div className="mt-2.5 rounded-xl border border-stone-200 p-3.5">
        <p className="text-[11.5px] text-stone-500">Total projektkostnad <span className="text-stone-400">(inkl. påslag, arbete, maskiner)</span></p>
        <p className="text-2xl font-bold op-num" data-testid="overview-project-total">{fmt(total)} kr</p>
        {budget ? (
          <>
            <div className="mt-2.5 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className="h-full bg-[#F26B1F] rounded-full" style={{ width: `${Math.min(100, (total / budget) * 100)}%` }} /></div>
            <p className="mt-1.5 text-[11px] text-stone-500 flex justify-between"><span>Av total budget</span><span className="op-num">{Math.round((total / budget) * 100)}%</span></p>
          </>
        ) : <p className="mt-1.5 text-[11px] text-stone-400">{labor ? 'Budget sätts när offerten godkänns.' : 'Arbete & maskiner läggs till efter Arbete-steget.'}</p>}
      </div>
      <div className="mt-3 divide-y divide-stone-100">
        <Row icon={Package} label="Material" value={material} pct={pct(material)} />
        <Row icon={Layers} label="Förbrukning & utrustning" value={equipment} pct={pct(equipment)} />
        <Row icon={HardHat} label="Arbete" value={work} pct={pct(work)} />
        <Row icon={Truck} label="Maskiner" value={machines} pct={pct(machines)} />
        <Row icon={Building2} label="Påslag" value={markup} pct={pct(markup)} />
      </div>
      <h4 className="mt-4 mb-2 text-[13px] font-bold">Nyckeltal</h4>
      <div className="grid grid-cols-2 gap-2">
        <KPI icon={Box} label="Byggnadsarea" value={area ? `${fmt(area)} m²` : '—'} testid="kpi-area" />
        <KPI icon={Ruler} label="Volym" value={stats ? `${fmt(stats.volume)} m³` : '—'} testid="kpi-volume" />
        <KPI icon={Building2} label="Antal våningar" value={stats?.floors || plan.floors || '—'} testid="kpi-floors" />
        <KPI icon={CalendarClock} label="Projektets längd" value={time ? `${time.total_workdays} arbetsdagar` : 'Beräknas i Tid'} testid="kpi-duration" />
      </div>
    </div>
  );
};
